// src/utils/appendZipState.js
// *******************************************************************************
// Append ZIP State to Form Values
// *******************************************************************************

import addressService from 'services/address/addressService';
import getValueFromFlattenObj from './getValueFromFlattenObj';

/**
 * Processes formValues by applying zipState decorations to address subdocuments
 *
 * For each key in zipState (e.g., 'zip', '__entity.zip', '__provider.billingAddress.zip'):
 * 1. Extracts the subdocument path by removing '.zip' suffix
 * 2. Gets the subdocument from formValues
 * 3. Decorates it using addressService.decorateAddressFromDatabase with zipState params
 * 4. Updates formValues with decorated address
 *
 * @param {Object} formValues - Form values object (nested structure)
 * @param {Object} zipState - ZIP state object with keys like 'zip', '__entity.zip', etc.
 * @returns {Object} Updated formValues with decorated address fields
 *
 * @example
 * const formValues = {
 *   name: "John",
 *   zip: "53990000",
 *   address1: "Rua X",
 *   __entity: {
 *     zip: "13010111",
 *     city: "Campinas"
 *   }
 * };
 *
 * const zipState = {
 *   zip: { apiFailure: true, searching: false },
 *   '__entity.zip': { apiFailure: false, searching: false }
 * };
 *
 * const result = appendZipState(formValues, zipState);
 * // result.addressMetadata.manual = true (root level)
 * // result.__entity.addressMetadata.manual = false (entity level)
 */
export const appendZipState = (formValues, zipState = {}) => {
  // zipState structure: { zip: { 'address.zip': {...}, 'entity.zip': {...} } }
  const zipStates = zipState?.zip || {};

  if (!zipStates || Object.keys(zipStates).length === 0) {
    return formValues;
  }

  // Clone formValues to avoid mutation
  const result = JSON.parse(JSON.stringify(formValues));

  // Process each zipState key (iterate over zipState.zip keys, not zipState keys)
  Object.keys(zipStates).forEach(flattenFieldName => {
    const zipStateValue = zipStates[flattenFieldName];

    // Remove '.zip' suffix to get the subdocument path
    // e.g., '__entity.zip' -> '__entity'
    //       'zip' -> '' (root level)
    //       '__provider.billingAddress.zip' -> '__provider.billingAddress'
    const subdocumentPath = flattenFieldName.endsWith('.zip')
      ? flattenFieldName.slice(0, -4) // Remove last 4 chars ('.zip')
      : flattenFieldName === 'zip'
        ? '' // Root level
        : flattenFieldName;

    // Get subdocument from formValues
    let subdocument = subdocumentPath ? getValueFromFlattenObj(result, subdocumentPath) : result; // Root level

    // If subdocument doesn't exist, create it in the result object
    // This handles cases where address fields haven't been filled yet
    if (!subdocument && subdocumentPath) {
      // Navigate/create the path in result
      const pathParts = subdocumentPath.split('.');
      let current = result;

      for (let i = 0; i < pathParts.length; i++) {
        const part = pathParts[i];
        if (!current[part]) {
          current[part] = {};
        }
        current = current[part];
      }

      subdocument = current;
    }

    if (!subdocument) {
      // This should only happen for root level with empty result, which is unexpected
      return;
    }

    // Decorate the subdocument with zipState params
    const decoratedAddress = addressService.decorateAddressFromDatabase(
      subdocument,
      zipStateValue,
      '' // No nested extraction needed, we already have the subdocument
    );

    if (!decoratedAddress) {
      return;
    }

    // IMPORTANT: Only merge fields that:
    // 1. Already exist in the subdocument (preserve user changes)
    // 2. OR are metadata fields (addressMetadata, addressAdditionalInfo, __city, __address)
    const metadataOnlyFields = [
      'addressMetadata',
      'addressAdditionalInfo',
      '__city',
      '__address',
    ];

    const filteredDecoratedAddress = {};
    Object.keys(decoratedAddress).forEach(key => {
      // Include if: it's a metadata field OR it already exists in subdocument
      if (metadataOnlyFields.includes(key) || subdocument.hasOwnProperty(key)) {
        filteredDecoratedAddress[key] = decoratedAddress[key];
      }
    });

    // Update formValues with filtered decorated address
    if (!subdocumentPath) {
      // Root level - merge directly
      Object.assign(result, filteredDecoratedAddress);
    } else {
      // Nested level - navigate and update
      const pathParts = subdocumentPath.split('.');
      let current = result;

      // Navigate to parent of target subdocument
      for (let i = 0; i < pathParts.length - 1; i++) {
        const part = pathParts[i];
        if (!current[part]) {
          current[part] = {};
        }
        current = current[part];
      }

      // Update the target subdocument
      const lastPart = pathParts[pathParts.length - 1];
      current[lastPart] = {
        ...current[lastPart],
        ...filteredDecoratedAddress,
      };
    }
  });

  return result;
};

export default appendZipState;
